import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Search, Star, Loader2, AlertCircle, Target, BookOpen, Users, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useReadingGoals } from '@/hooks/useReadingGoals';
import { StarRating } from '@/components/StarRating';
import { CoverImage } from '@/components/CoverImage';
import { OnboardingErrorCard } from '@/components/OnboardingErrorCard';
import { ReadingGoalSettings } from '@/components/ReadingGoalSettings';
import { PairwiseSheet } from '@/components/PairwiseSheet';
import { NotificationPreferences } from '@/components/NotificationPreferences';
import { FollowedTagsList } from '@/components/FollowedTagsList';
import { PrivacyModeSelector } from '@/components/PrivacyModeSelector';
import { supabase } from '@/integrations/supabase/client';
import { 
  getUserGenres,
  saveUserGenres,
  saveBookRatings
} from '@/lib/onboarding';
import { FICTION_GENRES, NONFICTION_GENRES } from '@/lib/constants';

const FICTION = FICTION_GENRES;
const NONFICTION = NONFICTION_GENRES;

interface BookData {
  id: string;
  title: string;
  authors: string[];
  cover_url?: string;
  isbn13?: string;
  open_library_id?: string;
  subjects?: string[];
}

export default function Preferences() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const { getGoalForYear, createOrUpdateGoal, loading: goalLoading } = useReadingGoals();

  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [bookRatings, setBookRatings] = useState<Record<string, number>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'title' | 'author'>('title');
  const [searchResults, setSearchResults] = useState<BookData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Reading goals state
  const currentYear = new Date().getFullYear();
  const [targetBooks, setTargetBooks] = useState<string>('12');
  const [targetPages, setTargetPages] = useState<string>('');
  const [currentGoal, setCurrentGoal] = useState<any>(null);
  
  // Pairwise ranking state
  const [showPairwise, setShowPairwise] = useState(false);
  const [pairwiseData, setPairwiseData] = useState<any>(null);
  const [currentRatingBook, setCurrentRatingBook] = useState<BookData | null>(null);

  // Privacy mode state
  const [privacyMode, setPrivacyMode] = useState<'public' | 'private' | 'ghost'>('private');
  const [privacyModeSaving, setPrivacyModeSaving] = useState(false);

  // Debug mode for dev
  const debugMode = import.meta.env.DEV && new URLSearchParams(location.search).has('debug');

  // Load user's existing preferences
  useEffect(() => {
    if (user) {
      loadUserPreferences();
    }
  }, [user]);

  const loadUserPreferences = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      // Load user genres
      const genres = await getUserGenres(user.id);
      setSelectedGenres(genres);

      // Load user's existing ratings (for quick search)
      await loadUserRatings();

      // Load current year's reading goal
      await loadCurrentGoal();

      // Load privacy mode
      const { data: profileData } = await supabase
        .from('profiles')
        .select('privacy_mode')
        .eq('id', user.id)
        .single();

      if (profileData?.privacy_mode) {
        setPrivacyMode(profileData.privacy_mode);
      }

    } catch (error) {
      console.error('Error loading user preferences:', error);
      setError('Failed to load your preferences. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentGoal = async () => {
    const goal = await getGoalForYear(currentYear);
    if (goal) {
      setCurrentGoal(goal);
      setTargetBooks(goal.target_books.toString());
      setTargetPages(goal.target_pages?.toString() || '');
    }
  };

  const loadUserRatings = async () => {
    if (!user) return;

    try {
      const { data } = await import('@/integrations/supabase/client').then(m => m.supabase
        .from('user_books')
        .select('book_id, rating_star')
        .eq('user_id', user.id)
        .not('rating_star', 'is', null)
      );

      if (data) {
        const ratings: Record<string, number> = {};
        data.forEach(item => {
          if (item.rating_star) {
            ratings[item.book_id] = item.rating_star;
          }
        });
        setBookRatings(ratings);
      }
    } catch (error) {
      console.error('Error loading user ratings:', error);
    }
  };

  const handleGenreToggle = async (genre: string) => {
    const newSelectedGenres = selectedGenres.includes(genre)
      ? selectedGenres.filter(g => g !== genre)
      : [...selectedGenres, genre];

    setSelectedGenres(newSelectedGenres);

    try {
      // Save genres immediately
      if (user) {
        await saveUserGenres(user.id, newSelectedGenres);
        toast({
          title: "Preferences updated",
          description: `${genre} ${newSelectedGenres.includes(genre) ? 'added to' : 'removed from'} your interests.`,
        });
      }
    } catch (error) {
      console.error('Error saving genres:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save preferences. Please try again.",
      });
      // Revert the change
      setSelectedGenres(selectedGenres);
    }
  };

  const handleRatingChange = async (bookId: string, rating: number) => {
    if (!user) return;

    // Update local state immediately (optimistic update)
    setBookRatings(prev => ({
      ...prev,
      [bookId]: rating
    }));

    try {
      // Save to database
      await saveBookRatings(user.id, [{ bookId, rating }]);
      
      // Trigger pairwise comparison immediately
      const { data, error } = await supabase.functions.invoke('on-rating-saved', {
        body: {
          userId: user.id,
          bookId,
          rating
        }
      });

      if (!error && data?.needsComparison && data?.comparison) {
        // Find the book details to pass to PairwiseSheet
        const bookDetails = searchResults.find(b => b.id === bookId);
        
        if (bookDetails) {
          setCurrentRatingBook(bookDetails);
          setPairwiseData(data);
          setShowPairwise(true);
        }
      }
    } catch (error) {
      console.error('Error saving rating:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save rating. Please try again.",
      });
      // Revert the rating on error
      setBookRatings(prev => {
        const updated = { ...prev };
        delete updated[bookId];
        return updated;
      });
    }
  };

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      performSearch(searchQuery, searchType);
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchType]);

  const performSearch = async (query: string, type: 'title' | 'author') => {
    if (!query.trim()) return;

    try {
      setSearchLoading(true);
      
      // Search using the database
      const { data, error } = await supabase
        .from('books')
        .select('id, title, authors, cover_url, isbn13, isbn10')
        .or(
          type === 'title' 
            ? `title.ilike.%${query}%`
            : `authors.cs.{${query}}`
        )
        .limit(10);

      if (error) throw error;
      
      const results: BookData[] = (data || []).map(book => ({
        id: book.id,
        title: book.title,
        authors: book.authors || [],
        cover_url: book.cover_url || undefined,
        isbn13: book.isbn13 || undefined
      }));

      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      toast({
        variant: "destructive",
        title: "Search failed",
        description: "Could not search for books. Please try again."
      });
    } finally {
      setSearchLoading(false);
    }
  };

  const handlePrivacyModeSave = async (newMode: 'public' | 'private' | 'ghost') => {
    if (!user) return;

    try {
      setPrivacyModeSaving(true);
      
      const { error } = await supabase
        .from('profiles')
        .update({ privacy_mode: newMode })
        .eq('id', user.id);

      if (error) throw error;

      setPrivacyMode(newMode);
      
      toast({
        title: "Privacy mode updated",
        description: `Your profile is now ${newMode === 'ghost' ? 'completely private' : newMode}.`,
      });
    } catch (error) {
      console.error('Error updating privacy mode:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update privacy mode. Please try again.",
      });
    } finally {
      setPrivacyModeSaving(false);
    }
  };

  const handleDone = () => {
    // Navigate back to previous page or profile
    const referrer = location.state?.from;
    if (referrer) {
      navigate(referrer);
    } else if (user) {
      // Try to get username for profile navigation
      import('@/integrations/supabase/client').then(m => m.supabase
        .from('profiles')
        .select('username')
        .eq('id', user.id)
        .single()
      ).then(({ data }) => {
        if (data?.username) {
          navigate(`/u/${data.username}`);
        } else {
          navigate('/');
        }
      }).catch(() => {
        navigate('/');
      });
    } else {
      navigate('/');
    }
  };

  const handleGoalSave = async () => {
    const booksTarget = parseInt(targetBooks);
    const pagesTarget = targetPages ? parseInt(targetPages) : undefined;
    
    if (isNaN(booksTarget) || booksTarget < 1) {
      toast({
        variant: "destructive",
        title: "Invalid Goal",
        description: "Please enter a valid number of books (at least 1).",
      });
      return;
    }

    if (targetPages && (isNaN(pagesTarget!) || pagesTarget! < 1)) {
      toast({
        variant: "destructive", 
        title: "Invalid Goal",
        description: "Please enter a valid number of pages or leave it empty.",
      });
      return;
    }

    const goal = await createOrUpdateGoal(currentYear, booksTarget, pagesTarget);
    if (goal) {
      setCurrentGoal(goal);
    }
  };

  if (!user) {
    navigate('/auth');
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-lg">Loading your preferences...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <OnboardingErrorCard
              title="Unable to load preferences"
              message={error}
              onRetry={() => {
                setError(null);
                loadUserPreferences();
              }}
              onBack={() => navigate(-1)}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-[var(--header-height)] z-40 bg-card border-b border-border">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <h1 className="text-xl font-heading font-semibold">Reading Preferences</h1>
          <div className="w-16" />
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          
          {/* Reading Goals Section */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                {currentYear} Reading Goal
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Set your reading targets for this year to track your progress.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="target-books">Books to read</Label>
                  <Input
                    id="target-books"
                    type="number"
                    min="1"
                    placeholder="12"
                    value={targetBooks}
                    onChange={(e) => setTargetBooks(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="target-pages">Pages to read (optional)</Label>
                  <Input
                    id="target-pages"
                    type="number"
                    min="1"
                    placeholder="5000"
                    value={targetPages}
                    onChange={(e) => setTargetPages(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  onClick={handleGoalSave} 
                  disabled={goalLoading}
                  size="sm"
                >
                  {goalLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {currentGoal ? 'Update Goal' : 'Set Goal'}
                </Button>
                {currentGoal && (
                  <span className="text-sm text-muted-foreground">
                    Current goal: {currentGoal.target_books} books
                    {currentGoal.target_pages ? `, ${currentGoal.target_pages} pages` : ''}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Privacy Mode Section */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Profile Privacy
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Control who can view your profile and reading activity
              </p>
            </CardHeader>
            <CardContent>
              <PrivacyModeSelector
                value={privacyMode}
                onChange={handlePrivacyModeSave}
                disabled={privacyModeSaving}
              />
            </CardContent>
          </Card>

          {/* Find Friends Section (Placeholder) */}
          <Card className="card-elevated border-dashed">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-primary" />
                Find Friends
                <Badge variant="secondary" className="ml-2">Coming Soon</Badge>
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Connect with friends and discover readers with similar tastes
              </p>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <UserPlus className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-sm">
                  This feature is currently under development. Soon you'll be able to:
                </p>
                <ul className="text-sm mt-4 space-y-2 max-w-md mx-auto text-left">
                  <li>• Sync contacts to find friends already on Dino</li>
                  <li>• Invite friends via email or social media</li>
                  <li>• Discover readers with similar book tastes</li>
                  <li>• Follow your favorite authors and reviewers</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Notification Preferences Section */}
          <NotificationPreferences />

          {/* Followed Tags Section */}
          <FollowedTagsList />

          {/* Weekly Reading Goal (Streak System) */}
          <ReadingGoalSettings />
          
          {/* Genres Section */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>Your Reading Interests</CardTitle>
              <p className="text-sm text-muted-foreground">
                Select genres you enjoy reading. We recommend at least 3 for better book suggestions.
                <span className="ml-2 font-medium">
                  {selectedGenres.length} selected
                </span>
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Fiction Section */}
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Fiction
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {FICTION.map((genre) => (
                      <Badge
                        key={genre}
                        variant={selectedGenres.includes(genre) ? "default" : "outline"}
                        className="cursor-pointer px-4 py-2 text-sm hover:scale-105 transition-transform"
                        onClick={() => handleGenreToggle(genre)}
                      >
                        {genre}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Non-Fiction Section */}
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Non-Fiction
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {NONFICTION.map((genre) => (
                      <Badge
                        key={genre}
                        variant={selectedGenres.includes(genre) ? "default" : "outline"}
                        className="cursor-pointer px-4 py-2 text-sm hover:scale-105 transition-transform"
                        onClick={() => handleGenreToggle(genre)}
                      >
                        {genre}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Search Section */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>Quick Search & Rate</CardTitle>
              <p className="text-sm text-muted-foreground">
                Search for any book and rate it quickly
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search for books..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant={searchType === 'title' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSearchType('title')}
                  >
                    Title
                  </Button>
                  <Button
                    variant={searchType === 'author' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSearchType('author')}
                  >
                    Author
                  </Button>
                </div>
              </div>

              {searchLoading && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  <span className="text-sm text-muted-foreground">Searching...</span>
                </div>
              )}

              {searchResults.length > 0 && (
                  <div className="space-y-2 border rounded-lg p-4">
                    <h4 className="font-medium text-sm">Search Results</h4>
                    <div className="grid gap-4">
                      {searchResults.map((book) => (
                        <div key={book.id} className="flex items-center gap-4 p-2 border rounded">
                          <div className="w-12 h-16 bg-muted rounded flex-shrink-0">
                            <CoverImage
                              bookId={book.id}
                              isbn13={book.isbn13}
                              title={book.title}
                              author={book.authors?.[0]}
                              size="xs"
                              className="w-12 h-16"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h5 className="font-medium text-sm truncate">{book.title}</h5>
                            <p className="text-xs text-muted-foreground truncate">
                              by {book.authors.join(', ')}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <StarRating
                              value={bookRatings[book.id] || 0}
                              onChange={(rating) => handleRatingChange(book.id, rating)}
                              size="sm"
                              showLabel={false}
                            />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Debug Info */}
          {debugMode && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Debug Info:</strong>
                <br />
                Selected genres: {selectedGenres.length}
                <br />
                Total ratings: {Object.keys(bookRatings).length}
                <br />
                Search query: "{searchQuery}" ({searchType})
                <br />
                Search results: {searchResults.length}
              </AlertDescription>
            </Alert>
          )}

          {/* Done Button */}
          <div className="sticky bottom-0 bg-background/80 backdrop-blur-sm border-t border-border py-4">
            <div className="flex justify-center">
              <Button
                onClick={handleDone}
                size="lg"
                className="px-8"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Done'
                )}
              </Button>
            </div>
          </div>
        </div>
      </main>
      
      {/* Pairwise comparison sheet */}
      {showPairwise && pairwiseData && currentRatingBook && (
        <PairwiseSheet
          isOpen={showPairwise}
          onClose={() => {
            setShowPairwise(false);
            setPairwiseData(null);
            setCurrentRatingBook(null);
          }}
          currentBook={{
            id: currentRatingBook.id,
            title: currentRatingBook.title,
            authors: currentRatingBook.authors,
            cover_url: currentRatingBook.cover_url
          }}
          tier={pairwiseData.tier}
          candidates={[]}
          onComplete={() => {
            setShowPairwise(false);
            setPairwiseData(null);
            setCurrentRatingBook(null);
          }}
          onSkip={() => {
            setShowPairwise(false);
            setPairwiseData(null);
            setCurrentRatingBook(null);
          }}
          placementState={pairwiseData.placementState}
          comparison={pairwiseData.comparison}
        />
      )}
    </div>
  );
}